// 管理者用：生徒の出席不可コマ設定（複数講選択・解除対応）

import React, { useEffect, useState } from "react";
import { getRequest, postRequest } from "../../apiWrapper";
import { formatDate, getDatesBetween, toJSTDate } from "../../utils/formatDate";
import { toast } from "react-toastify";
import { FaSave, FaTrash, FaArrowUp } from "react-icons/fa";

const periods = [1, 2, 3, 4, 5, 6, 7];

const AdminStudentUnavailability = () => {
    const handleSelectAll = (dates) => {
        const newMap = { ...unavailableMap };
        dates.forEach(date => {
            periods.forEach(period => {
                if (!isHoliday(date, period)) {
                    if (!newMap[date]) newMap[date] = new Set();
                    newMap[date].add(period);
                }
            });
        });
        setUnavailableMap(newMap);
    };
    const [students, setStudents] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [unavailableMap, setUnavailableMap] = useState({});
    const [dateChunks, setDateChunks] = useState([]);
    const [regularHolidays, setRegularHolidays] = useState([]);
    const [specialHolidays, setSpecialHolidays] = useState([]);
    const [entryWindow, setEntryWindow] = useState(null);

    useEffect(() => {
        const fetchInitial = async () => {
            try {
                const res = await getRequest("students?excludeWithdrawn=true");
                setStudents(res.students || []);

                const reg = await getRequest("schoolRegularHolidays");
                setRegularHolidays(reg.holidays || []);

                const spec = await getRequest("schoolSpecialHolidays");
                setSpecialHolidays(spec.holidays || []);

                const winRes = await getRequest("shiftEntryWindows");
                const wins = winRes.windows || [];
                if (wins.length) {
                    wins.sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
                    const latest = wins[0];
                    setEntryWindow(latest);

const allDates = getDatesBetween(latest.start_date, latest.end_date);
const chunked = [];

for (let i = 0; i < allDates.length; i += 35) {
  const chunk = allDates.slice(i, i + 35); // ← ここを修正
  chunked.push(chunk);
}

setDateChunks(chunked);

                }
            } catch (err) {
                toast.error("初期データ取得失敗");
                console.error(err);
            }
        };
        fetchInitial();
    }, []);

    const fetchUnavailabilities = async (studentid) => {
        try {
            const res = await getRequest(`studentUnavailabilities?studentid=${studentid}`);
            const map = {};
            res.unavailabilities.forEach(({ date, period }) => {
                const d = toJSTDate(date).toISOString().split("T")[0];
                if (!map[d]) map[d] = new Set();
                map[d].add(period);
            });
            setUnavailableMap(map);
        } catch (err) {
            console.error(err);
        }
    };

    const handleStudentSelect = async (e) => {
        const studentid = e.target.value;
        const student = students.find(s => s.id === studentid);
        setSelectedStudent(student);
        if (student) await fetchUnavailabilities(student.id);
    };

    const isHoliday = (date, period) => {
        const dow = new Date(date).getDay();
        const isRegular = regularHolidays.some(h => h.day_of_week === dow && (h.period === null || h.period === period));
        const isSpecial = specialHolidays.some(h => {
            const holidayStr = toJSTDate(h.date).toISOString().split("T")[0];
            return holidayStr === date && (h.period === null || h.period === period);
        });
        return isRegular || isSpecial;
    };

    const toggleCell = (date, period) => {
        if (isHoliday(date, period)) return;
        setUnavailableMap(prev => {
            const newMap = { ...prev };
            const currentSet = new Set(prev[date] || []);
            if (currentSet.has(period)) {
                currentSet.delete(period);
                if (currentSet.size === 0) {
                    delete newMap[date];
                } else {
                    newMap[date] = currentSet;
                }
            } else {
                currentSet.add(period);
                newMap[date] = currentSet;
            }
            return newMap;
        });
    };

    const handleSave = async () => {
        if (!selectedStudent) return;
        const payload = [];
        Object.entries(unavailableMap).forEach(([date, set]) => {
            set.forEach(p => payload.push({ date, period: p }));
        });
        try {
            await postRequest("studentUnavailabilities", {
                studentid: selectedStudent.id,
                unavailabilities: payload
            });
            toast.success("保存しました");
        } catch (err) {
            console.error(err);
            toast.error("保存失敗");
        }
    };

    const handleClear = async () => {
        if (!selectedStudent) return;
        const payload = [];
        Object.entries(unavailableMap).forEach(([date, set]) => {
            set.forEach(p => payload.push({ date, period: p }));
        });
        if (!window.confirm("本当にすべて削除しますか？")) return;
        try {
            await postRequest("studentUnavailabilities/deleteBulk", {
                studentid: selectedStudent.id,
                unavailabilities: payload
            });
            setUnavailableMap({});
            toast.success("削除しました");
        } catch (err) {
            console.error(err);
            toast.error("削除失敗");
        }
    };

    return (
        <div className="p-4 pt-20">
            <h1 className="text-2xl font-bold mb-4">生徒の出席不可コマ設定</h1>

            <select className="p-2 border rounded mb-6" onChange={handleStudentSelect} value={selectedStudent?.id || ""}>
                <option value="">生徒を選択</option>
                {students.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                ))}
            </select>

            {selectedStudent && dateChunks.map((dates, idx) => (
                <div key={idx} className="overflow-x-auto mb-10">
                    <div className="mb-2">
                        <button
                            onClick={() => handleSelectAll(dates)}
                            className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 text-sm"
                        >
                            この表のすべてを選択
                        </button>
                    </div>
                    <table className="min-w-max border text-xs">
                        <thead>
                            <tr>
                                <th className="p-1 border bg-gray-100">講＼日</th>
                                {dates.map(d => (
                                    <th key={d} className="p-1 border bg-gray-50 whitespace-nowrap">{d.slice(5)}<br />({"日月火水木金土"[new Date(d).getDay()]})</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {periods.map(p => (
                                <tr key={p}>
                                    <td className="p-1 border font-bold bg-gray-100">{p}講</td>
                                    {dates.map(d => {
                                        const selected = unavailableMap[d]?.has(p);
                                        const holiday = isHoliday(d, p);
                                        return (
                                            <td
                                                key={d + p}
                                                className={`w-10 h-10 border text-center cursor-pointer text-xs transition-all duration-100
                          ${holiday ? "bg-gray-300 text-gray-400 pointer-events-none" : selected ? "bg-red-500 text-white" : "bg-white hover:bg-red-200"}`}
                                                onClick={() => toggleCell(d, p)}
                                            >
                                                {selected ? "×" : ""}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}

            {selectedStudent && (
                <div className="flex gap-4 mt-6">
                    <button onClick={handleSave} className="bg-green-600 text-white px-4 py-2 rounded flex items-center gap-2 text-sm">
                        <FaSave /> 保存する
                    </button>
                    <button onClick={handleClear} className="bg-red-600 text-white px-4 py-2 rounded flex items-center gap-2 text-sm">
                        <FaTrash /> 全削除
                    </button>
                </div>
            )}
        </div>
    );
};

export default AdminStudentUnavailability;
